/**
 * Transcription Service
 * Handles audio transcription operations
 */

import { AudioPodClient } from '../client/AudioPodClient';
import {
  TranscriptionRequest,
  TranscriptionResult,
  Job
} from '../types';

export class TranscriptionService {
  constructor(private client: AudioPodClient) {}

  /**
   * Transcribe audio to text
   */
  async transcribeAudio(request: TranscriptionRequest): Promise<TranscriptionResult> {
    const {
      audioFile,
      language,
      modelType = 'whisperx',
      enableSpeakerDiarization = false,
      enableWordTimestamps = true,
      waitForCompletion = false,
      timeout = 600000
    } = request;

    let fileData: File | Blob;

    if (typeof audioFile === 'string') {
      const fs = await import('fs');
      const path = await import('path');

      if (!fs.existsSync(audioFile)) {
        throw new Error(`Audio file not found: ${audioFile}`);
      }

      const fileBuffer = fs.readFileSync(audioFile);
      const fileName = path.basename(audioFile);
      fileData = new Blob([fileBuffer], { type: 'audio/*' });
      (fileData as any).name = fileName;
    } else {
      fileData = audioFile;
    }

    const formData: Record<string, any> = {
      model_type: modelType,
      enable_speaker_diarization: enableSpeakerDiarization,
      enable_word_timestamps: enableWordTimestamps
    };

    if (language) {
      formData.language = language.toLowerCase();
    }

    const job = await this.client.uploadFile<Job>(
      '/transcription/transcribe-upload',
      fileData,
      formData
    );

    if (waitForCompletion) {
      const result = await this.client.waitForJobCompletion(job.id, timeout);
      return {
        job: { ...job, ...result },
        transcript: result.transcript,
        detectedLanguage: result.detected_language || result.detectedLanguage,
        confidenceScore: result.confidence_score || result.confidenceScore,
        segments: result.segments,
        audioDuration: result.total_duration || result.audioDuration
      };
    }

    return { job };
  }

  /**
   * Transcribe from URL
   */
  async transcribeUrl(
    url: string,
    options: {
      language?: string;
      modelType?: 'whisperx' | 'faster-whisper';
      enableSpeakerDiarization?: boolean;
      waitForCompletion?: boolean;
      timeout?: number;
    } = {}
  ): Promise<TranscriptionResult> {
    const {
      language,
      modelType = 'whisperx',
      enableSpeakerDiarization = false,
      waitForCompletion = false,
      timeout = 600000
    } = options;

    const data: Record<string, any> = {
      source_urls: [url],
      model_type: modelType,
      enable_speaker_diarization: enableSpeakerDiarization
    };

    if (language) {
      data.language = language.toLowerCase();
    }

    const job = await this.client.post<Job>('/transcription/transcribe', data);

    if (waitForCompletion) {
      const result = await this.client.waitForJobCompletion(job.id, timeout);
      return {
        job: { ...job, ...result },
        transcript: result.transcript,
        detectedLanguage: result.detected_language || result.detectedLanguage,
        confidenceScore: result.confidence_score || result.confidenceScore,
        segments: result.segments,
        audioDuration: result.total_duration || result.audioDuration
      };
    }

    return { job };
  }

  /**
   * Get transcription job
   */
  async getTranscriptionJob(jobId: number): Promise<TranscriptionResult> {
    const job = await this.client.get<any>(`/transcription/jobs/${jobId}`);

    return {
      job,
      transcript: job.transcript,
      detectedLanguage: job.detected_language || job.detectedLanguage,
      confidenceScore: job.confidence_score || job.confidenceScore,
      segments: job.segments,
      audioDuration: job.total_duration || job.audioDuration
    };
  }

  /**
   * Download transcript
   */
  async downloadTranscript(
    jobId: number,
    format: 'json' | 'txt' | 'srt' | 'vtt' | 'pdf' | 'docx' | 'html' = 'json'
  ): Promise<string> {
    return this.client.get(`/transcription/jobs/${jobId}/transcript`, { format });
  }
}
